/**
 * API Endpoint Utilities
 * Provides centralized access to API endpoints and tokens
 */

import { useApiEndpointStore } from '../store/apiEndpointStore';

/**
 * API Endpoint Configuration Interface
 */
export interface ApiEndpointConfig {
  apiGateway: string;
  iotApiGateway: string;
  gpsWebSocketUrl: string;
  eventWebSocketUrl: string;
}

/**
 * Get API endpoint configuration from store
 * @returns ApiEndpointConfig or null if not initialized
 */
export function getApiEndpointConfig(): ApiEndpointConfig | null {
  const state = useApiEndpointStore.getState();
  
  if (!state.isInitialized || !state.config) {
    return null;
  }
  
  return state.config;
}

/**
 * Check if API endpoints are configured
 * @returns True if endpoints are configured
 */
export function isApiEndpointConfigured(): boolean {
  return getApiEndpointConfig() !== null;
}

/**
 * Get specific API endpoint
 * @param endpoint - Endpoint type to retrieve
 * @returns Endpoint URL or null if not configured
 */
export function getApiEndpoint(endpoint: keyof ApiEndpointConfig): string | null {
  const config = getApiEndpointConfig();
  
  if (!config) {
    console.error('❌ [EndpointUtils] API endpoints not configured. Please initialize in main.tsx');
    return null;
  }
  
  return config[endpoint];
}

/**
 * Get API Gateway URL
 * @returns API Gateway URL or throws error if not configured
 */
export function getApiGateway(): string {
  const url = getApiEndpoint('apiGateway');
  
  if (!url) {
    throw new Error('API Gateway not configured. Please set VITE_API_GATEWAY in .env file.');
  }
  
  return url;
}

/**
 * Get IoT API Gateway URL
 * @returns IoT API Gateway URL or throws error if not configured
 */
export function getIotApiGateway(): string {
  const url = getApiEndpoint('iotApiGateway');
  
  if (!url) {
    throw new Error('IoT API Gateway not configured. Please set VITE_IOT_API_GATEWAY in .env file.');
  }
  
  return url;
}

/**
 * Get GPS WebSocket URL
 * @returns GPS WebSocket URL or throws error if not configured
 */
export function getGpsWebSocketUrl(): string {
  const url = getApiEndpoint('gpsWebSocketUrl');
  
  if (!url) {
    throw new Error('GPS WebSocket URL not configured. Please set VITE_GPS_WEBSOCKET_URL in .env file.');
  }
  
  return url;
}

/**
 * Get Event WebSocket URL
 * @returns Event WebSocket URL or throws error if not configured
 */
export function getEventWebSocketUrl(): string {
  const url = getApiEndpoint('eventWebSocketUrl');
  
  if (!url) {
    throw new Error('Event WebSocket URL not configured. Please set VITE_EVENT_WEBSOCKET_URL in .env file.');
  }
  
  return url;
}

/**
 * Get API endpoint configuration error message
 * @returns Standard endpoint configuration error message
 */
export function getEndpointErrorMessage(): string {
  return 'API endpoints not configured. Please check your environment variables.';
}

/**
 * Validate all required endpoints are configured
 * @returns True if all required endpoints are configured
 */
export function validateApiEndpoints(): boolean {
  const config = getApiEndpointConfig();
  
  if (!config) {
    return false;
  }
  
  const requiredEndpoints: (keyof ApiEndpointConfig)[] = [
    'apiGateway',
    'iotApiGateway',
    'gpsWebSocketUrl',
    'eventWebSocketUrl'
  ];
  
  return requiredEndpoints.every(endpoint => {
    const value = config[endpoint];
    return value && value.trim().length > 0;
  });
}